import { Link } from 'react-router-dom'
import { ArrowLeft, Image } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToImageGallery } from '@/components/gallery/TextToImageGallery'
import { ImageGalleryItem } from '@/models/gallery'
import { TextImageToImageGallery } from '@/components/gallery/TextImageToImageGallery'
import { styles } from '@/styles/ImagePage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Wan-2.2 Text-to-Image-Realism',
    shortName: 'Wan',
    modelKey: 'wan-image',
    creditsPerImage: 2.5,
    heroBgImage: 'https://cdn.aibrush.co/docs/images/models/wan-image/image-hero.jpg',
    providerUrl: 'https://wan.video/',
    providerName: 'Alibaba Cloud Wan',
    pageUrl: 'https://docs.aibrush.co/images-models/wan-image',
} as const;

const textToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "wan-image-1",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-1.jpg",

        prompt: "A school of manta rays swimming in the clear blue waters, sunlight filtering through their wings. An underwater photograph taken with a wide-angle lens, capturing the scene in high resolution and detail, resulting in a hyper-realistic image reminiscent of a National Geographic photo."
    },
    // {
    //     id: "wan-image-2",
    //     width: 1536,
    //     height: 864,
    //     src: "/images/models/wan-image/gallery-2.jpg",
    //     className: "gallery-item featured",
    //     prompt: "Ultra realistic macro photography showcasing tiny, detailed construction workers actively building a luxurious salmon sandwich atop a hearty bread slice. A detailed miniature crane is carefully lifting and positioning a premium slice of salmon onto the bread, operators directing the maneuver with precision. Nearby, other tiny workers expertly place fresh parsley garnish, arrange greens with minuscule tweezers, and enhance visual presentation, wearing clearly defined construction uniforms and helmets. Cinematic and dramatic lighting emphasizes impeccable textures of salmon, bread, parsley, with superb realism. Scene highly detailed, photorealistic, 8K resolution, volumetric lighting, depth of field, ultra sharp focus"
    // },
    {
        id: "wan-image-3",
        width: 864,
        height: 1536,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-3.jpg",

        prompt: "A realistic photograph of a purple tattooed penguin looking directly at a car mirror, fireworks visible in the distance, wearing white headphones, capturing both the fireworks in the sky and the reflection of streetlights, head poking out of a moving car window, reflection of the penguin's head visible in the car mirror, dark street background, night sky, night time, authentic photographic quality with natural grain, high ISO, low lighting, slightly out of focus, gritty, realistic noise, authentic texture, lifelike penguin with detailed feathers and natural coloring"
    },
    {
        id: "wan-image-4",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-4.jpg",

        prompt: "A low-angle shot of an angelic woman with short red hair in a black dress running through a forest, white daisies and petals flying around her. The imagery is blurry, with a glitchy video-like appearance, distorted perspective, and a fisheye lens effect. The focus is sharp, with a deep depth of field and an extreme wide-angle, creating a hyper-realistic, ultra-detailed, and cinematic scene. This image was captured in the style of Tim Walker."
    },
    {
        id: "wan-image-9",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-9.jpg",
        variant: 'tall',
        prompt: "A realistic photograph of a street-style girl with long flowing hair, wearing a mini skirt and stylish streetwear jacket, skateboarding and waving hands at high speed downhill on a winding mountain road, a breathtaking futuristic cityscape and vast ocean far ahead under a twilight sky with natural auroras, modern billboards in the distance, flower petals, wind particles, and natural light trails, cinematic lighting with dramatic shadows and lens flares, ultra low-angle fixed camera behind her, face not visible, natural skateboarding posture with arms spread, knees slightly bent but more extended than crouching, long hair and outfit blown wildly by the wind, reflective road surface, natural skateboard edges, sense of immense speed and scale, photorealistic quality"
    },
    {
        id: "wan-image-6",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-6.jpg",

        prompt: "genderless pale blue skin human being, looking at holographic data predictions, surreal and contemplative, French science fiction aesthetic, soft 70s colors, digital consciousness emerging"
    },
    {
        id: "wan-image-5",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-5.jpg",
        variant: 'wide',
        prompt: "Create the image of a fly in Disney Pixar style. Slowly pan camera up to reveal Large eyes, Large wings, chubby with Nike Air max running shoes on all feet. Facial expression is sly. Fly lands crossing arms in a hip hop stance."
    },
    {
        id: "wan-image-7 wide",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-7.jpg",
        variant: 'wide',
        prompt: "Cinematic 35mm wide shot of deer and birds fleeing a burning forest, slow tracking shot following the running animals from behind, fire glow in the background, smoke swirling, naturalistic movement with cinematic grading, 8K hyperrealistic render"
    },
    {
        id: "wan-image-8",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-8.jpg",

        prompt: "six year old human girl playing a soldier in middle of a busy city street in fantasy setting, wielding a wooden sword"
    },
    {
        id: "wan-image-10",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-10.jpg",

        prompt: "A realistic photograph of an African man with natural afro hair style, lying on a worn-out cozy bed, holding a small cracked phone to his ear as if making a phone call, wearing simple worn-out blue and white shirt, faded trousers, old sneakers with scratches, slightly tired expression, humble and modest look, soft natural lighting, authentic photographic style, small simple room background with muted tones, lifelike details and natural skin texture"
    },
    {
        id: "wan-image-14",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-14.jpg",

        prompt: "Experimental video, in the style of Ohio academia, high detail, close-up of the upper body, smiling tennis player. Aerial shot, black and white sports skirt with navy blue accents, blonde hair in a ponytail, wearing a visor hat, holding a racket, smiling at the camera, standing on the court."
    },
    {
        id: "wan-image-11",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-11.jpg",

        prompt: "A chubby, cute cat preparing various ingredients inside a Subway sandwich shop. The cat looks just like a real person. The cat is wearing a green Subway apron and has headphones on its ears."
    },
    {
        id: "wan-image-12",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-12.jpg",

        prompt: "Outdoor photoshoot lookbook, high angle image, male model wearing cargo shorts and NIKE W COURT VISION LO Black+White sneakers, highlighted by the sneakers, the model is sitting on the floor hugging his legs, the floor is a concrete skate park, natural sunset lighting, warm golden lighting, hiper detailed hands, hiperdetailed, hiperrealistic, anatomic correct, photographed with a Canon, 80mm, f2.0"
    },
    {
        id: "wan-image-13",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-13.jpg",

        prompt: "a smiling glamorous close up of a beautiful woman with a glittery diamond earring. on a white background to be used for marketing."
    },
];

const textImageToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "wan-image-15",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-15.jpg",

        prompt: "it's snowing",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-4.jpg"]
    },
    {
        id: "wan-image-16",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-16.jpg",

        prompt: "Make the cat wear McDonald's clothes, and remove the headphones",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-11.jpg"]
    },
    {
        id: "wan-image-17",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-17.jpg",

        prompt: "The photo becomes a picture in a museum, and people around are amazed.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-1.jpg"]
    },
    {
        id: "wan-image-18",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-18.jpg",
        variant: 'wide',
        prompt: "Remove the fire and make the photo colorful.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-7.jpg"]
    },
    {
        id: "wan-image-19",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-19.jpg",

        prompt: "Make the photo in the style of Game of Thrones, and the girl with a dragon",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-8.jpg"]
    },
    {
        id: "wan-image-20",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-20.jpg",

        prompt: "Make the photo ultra-realistic photography a city center",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/wan-image/gallery-9.jpg"]
    },
];

const allTextImageToImage: string[] = [
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-1.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-4.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-7.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-8.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-9.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-11.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-15.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-16.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-17.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-18.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-19.jpg",
    "https://cdn.aibrush.co/docs/images/models/wan-image/gallery-20.jpg",
];

export default function WanImagePage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroBackground)}>
                    <img
                        src={MODEL_CONFIG.heroBgImage}
                        {...stylex.props(styles.heroBgImage)}
                        alt={`${MODEL_CONFIG.name} hero image`}
                    />
                    <div {...stylex.props(styles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.shortName.toUpperCase()} IMAGES</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Best for <strong>Realistic Photography</strong>. Generate super realistic images from text with {MODEL_CONFIG.shortName}. Supports <strong>Text to Image</strong> and <strong>Text & Image to Image</strong> capabilities.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text & Image to Image</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerImage} credits per image</div>
                </div>
            </section>

            {/* Image Gallery */}
            <TextToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text to Image Gallery"
                description={`Explore photorealism and natural detail for creating lifelike photographs with <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a>. <strong>Click on any image</strong> to see how it was created.`}
                items={textToImageGalleryItems} />

            {/* Text & Image to Image Gallery */}
            <TextImageToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text & Image to Image Gallery"
                description={`While ${MODEL_CONFIG.name} excels at generating realistic photography, for image editing we leverage the power of <a href='https://lumalabs.ai/photon' target='_blank' rel='noopener noreferrer'>Luma Photon</a> under the hood. When you select image reference with ${MODEL_CONFIG.shortName}, Luma Photon advanced editing models handle the transformation seamlessly. <strong>Click on any image</strong> to see how it was created.`}
                items={textImageToImageGalleryItems}
                allImages={allTextImageToImage}
            />
        </div>
    )
}

